import { useEffect, useRef, useState } from 'react'

import copy from '../../demo/fixtures/w5/copy.json'
import { useDemoStore, type W5ReportExport } from '../../demo/store'
import { Button, GlossaryText, Modal } from '../../ui'
import { ProcessSteps, type ProcessStep } from './ProcessSteps'
import { fillTemplate, type W5Live } from './live'
import { useProcessRun } from './useProcessRun'
import styles from './AnalysisPage.module.css'

interface ReportPreviewDialogProps {
  open: boolean
  report: W5Live['report']
  taskRows: W5Live['taskRows']
  onClose: () => void
  onDone: (item: W5ReportExport) => void
}

function processSteps(report: W5Live['report'], taskRows: W5Live['taskRows'], item: W5ReportExport): ProcessStep[] {
  const kpis = new Map(report.kpis.map((kpi) => [kpi.label, kpi.value]))
  const vars = {
    taskCount: taskRows.length,
    taskNames: `${taskRows.slice(0, 2).map((task) => task.product).join('、')}等 ${taskRows.length} 个`,
    spend: kpis.get('花费') ?? '—',
    orders: kpis.get('转化订单') ?? '—',
    revenue: kpis.get('销售额') ?? '—',
    findingCount: report.findings.length,
    findingTitles: `${report.findings.slice(0, 2).map((finding) => finding.title).join('、')}…`,
    issue: report.issue,
    pages: report.pages,
    range: report.range,
    grade: report.grade,
    seq: item.seq,
  }
  return copy.process.exportPdf.map((step) => ({
    label: fillTemplate(step.label, vars),
    detail: fillTemplate(step.detail, vars),
  }))
}

export function ReportPreviewDialog({ open, report, taskRows, onClose, onDone }: ReportPreviewDialogProps) {
  const appendReportExport = useDemoStore((state) => state.appendReportExport)
  const [ready, setReady] = useState(false)
  const [exportItem, setExportItem] = useState<W5ReportExport | null>(null)
  const exportRef = useRef<W5ReportExport | null>(null)
  const inputRef = useRef({ report, taskRows })
  const { visibleSteps, running, run, cancel } = useProcessRun()

  useEffect(() => {
    if (!open) return
    let active = true
    const item = exportRef.current ?? appendReportExport()
    exportRef.current = item
    setExportItem(item)
    setReady(false)
    const source = inputRef.current
    void run(processSteps(source.report, source.taskRows, item), copy.process).then((completed) => {
      if (active && completed) setReady(true)
    })
    return () => { active = false; cancel() }
  }, [appendReportExport, cancel, open, run])

  const finish = () => {
    if (!exportItem) return
    onDone(exportItem)
    onClose()
  }

  return (
    <Modal open={open} title={`${report.title} · ${report.issue}`} hint={`${report.range} · 生成于 ${exportItem?.at ?? report.made}`} onClose={onClose} footer={ready ? <Button onClick={finish}>完成</Button> : undefined}>
      {running ? <ProcessSteps steps={visibleSteps} /> : null}
      {ready && exportItem ? (
        <article className={styles.reportPaper}>
          <div className={styles.paperHead}><b>{report.issue}</b><span>{report.range}</span><strong>评级 {report.grade}</strong></div>
          <p className={styles.paperLead}><GlossaryText text={report.lead} /></p>
          <div className={styles.paperKpis}>{report.kpis.map((kpi) => <div key={kpi.label}><span><GlossaryText text={kpi.label} /></span><b>{kpi.value}</b><small>{kpi.delta}</small></div>)}</div>
          <h3>诊断发现</h3>
          <ol>{report.findings.map((finding) => <li key={finding.id}><b><GlossaryText text={finding.title} /></b><span>{finding.evidence?.value} · <GlossaryText text={finding.evidence?.sub ?? ''} /></span></li>)}</ol>
          <h3>下周行动</h3>
          <ul>{report.actions.map((action) => <li key={`${action.owner}-${action.text}`}><GlossaryText text={action.text} /> · {action.when}</li>)}</ul>
          <p className={styles.paperFoot}>{report.foot} · 导出 #{exportItem.seq} · 生成于 {exportItem.at} · 共 {report.pages} 页</p>
        </article>
      ) : null}
    </Modal>
  )
}
